import "../../../styles/antd-overrides.css";
import React, { useContext, useState, useEffect } from "react";
import { Tabs } from "antd";
import type { TabsProps } from "antd";
import { ReportFilters } from "./ReportFilters";
import { ReportTable } from "./ReportTable";
import { useAllFinancialDataHook } from "../../../hooks/useFetchFinancialData.hook";
import { StateContext } from "../../../store/context";
import { ChevronDown, ChevronUp } from "lucide-react";

interface FinancialReportsProps {
  selectedStock?: any;
  profileData?: any;
  onTabChange?: (tabKey: string) => void;
}

interface Filters {
  stock_id: number | null;
  reverseDate: boolean | null;
  isQuarterly: boolean | null;
  baseCurrency: string | null;
  showMargins: boolean | null;
  reportType: number | null;
  selectedPeriod: string | null;
}

interface SwitchStates {
  reverseDate: boolean;
  showMargins: boolean;
}

const TAB_TO_REPORT_TYPE: { [key: string]: number } = {
  income_statement: 1,
  balance_sheet: 2,
  cash_flow: 3,
};

export const FinancialReports: React.FC<FinancialReportsProps> = ({
  selectedStock,
  profileData,
  onTabChange: onTabChangeCallback,
}) => {
  const {
    baseCurrency,
    authUser: { role_name, coupon_code },
  } = useContext(StateContext);

  const [viewMode, setViewMode] = useState<"quarterly" | "annually">(
    "quarterly",
  );
  const [selectedPeriod, setSelectedPeriod] = useState("1");
  const [activeTab, setActiveTab] = useState<string>("income_statement");

  const [activeDecimalFormat, setActiveDecimalFormat] = useState<string>("");
  const [activeScaleFormat, setActiveScaleFormat] = useState<string>("");

  // Track if user has ever interacted with decimal format
  const [hasDecimalBeenModified, setHasDecimalBeenModified] =
    useState<boolean>(false);

  const [formatConfig, setFormatConfig] = useState<{
    decimal: string;
    scale: string;
  }>({
    decimal: "",
    scale: "",
  });

  const [switchStates, setSwitchStates] = useState<SwitchStates>({
    reverseDate: false,
    showMargins: false,
  });

  const [filters, setFilters] = useState<Filters>({
    stock_id: selectedStock?.id || selectedStock?.stock_id || null,
    reverseDate: null,
    isQuarterly: true,
    baseCurrency: baseCurrency || null,
    showMargins: null,
    reportType: TAB_TO_REPORT_TYPE[activeTab] || null,
    selectedPeriod: "1",
  });

  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);

  useEffect(() => {
    if (selectedStock) {
      setFilters({
        stock_id: selectedStock.id || selectedStock.stock_id,
        reverseDate: switchStates.reverseDate,
        isQuarterly: viewMode === "quarterly",
        baseCurrency: baseCurrency || null,
        showMargins: switchStates.showMargins,
        reportType: TAB_TO_REPORT_TYPE[activeTab],
        selectedPeriod: selectedPeriod,
      });
    }
  }, [
    selectedStock,
    viewMode,
    baseCurrency,
    switchStates,
    activeTab,
    selectedPeriod,
  ]);

  const cleanFilters = (filters: Filters) => {
    const cleaned: { [key: string]: string | number | boolean } = {};

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        cleaned[key] = value;
      }
    });

    return cleaned;
  };

  const cleanedFilters = cleanFilters(filters);
  const queryParams = new URLSearchParams(
    Object.entries(cleanedFilters).map(([key, value]) => [key, String(value)]),
  ).toString();

  const shouldFetch = Boolean(filters.stock_id);

  const { data, error, isLoading } = useAllFinancialDataHook(
    shouldFetch ? queryParams : null,
  );

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    if (onTabChangeCallback) {
      onTabChangeCallback(key);
    }
  };

  const handlePeriodChange = (value: string) => {
    setSelectedPeriod(value);
  };

  const handleViewModeChange = (checked: boolean) => {
    setViewMode(checked ? "annually" : "quarterly");
  };

  const handleReverseDateChange = (checked: boolean) => {
    setSwitchStates((prev) => ({ ...prev, reverseDate: checked }));
  };

  const handleShowMarginsChange = (checked: boolean) => {
    setSwitchStates((prev) => ({ ...prev, showMargins: checked }));
  };

  const handleFormatChange = (config: { decimal: string; scale: string }) => {
    if (config.decimal !== formatConfig.decimal) {
      setHasDecimalBeenModified(true);
    }
    setFormatConfig(config);
    setActiveDecimalFormat(config.decimal);
    setActiveScaleFormat(config.scale);
  };

  const isPremiumUser: boolean = role_name === "Premium Customer";
  const isBasicCustomer: boolean = role_name === "Basic Customer";

  const isDisabledPeriods: boolean =
    (isPremiumUser || isBasicCustomer) && coupon_code !== "CFADOHA";

  const periodOptions = [
    // { value: "5", label: "All Years" },
    { value: "1", label: "1 Year" },
    {
      value: "3",
      label: "3 Years",
      // disabled: isDisabledPeriods,
    },
    {
      value: "5",
      label: "5 Years",
      disabled: isDisabledPeriods,
    },
  ];

  const renderReportsContent = (selectedStock: any) => (
    <div className="space-y-4">
      <ReportTable
        activeTab={activeTab}
        data={selectedStock ? data : null}
        viewMode={viewMode}
        formatConfig={formatConfig}
        currency={baseCurrency}
        loading={isLoading}
        hasDecimalBeenModified={hasDecimalBeenModified}
      />
    </div>
  );

  const tabItems: TabsProps["items"] = [
    {
      key: "income_statement",
      label: "Income Statement",
      children: (
        <ReportFilters
          selectedStock={selectedStock}
          selectedPeriod={selectedPeriod}
          onPeriodChange={handlePeriodChange}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          periodOptions={periodOptions}
          reverseDate={switchStates.reverseDate}
          onReverseDateChange={handleReverseDateChange}
          showMargins={switchStates.showMargins}
          onShowMarginsChange={handleShowMarginsChange}
          onFormatChange={handleFormatChange}
          activeDecimalFormat={activeDecimalFormat}
          activeScaleFormat={activeScaleFormat}
        >
          {renderReportsContent(selectedStock)}
        </ReportFilters>
      ),
    },
    {
      key: "balance_sheet",
      label: "Balance Sheet",
      children: (
        <ReportFilters
          selectedStock={selectedStock}
          selectedPeriod={selectedPeriod}
          onPeriodChange={handlePeriodChange}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          periodOptions={periodOptions}
          reverseDate={switchStates.reverseDate}
          onReverseDateChange={handleReverseDateChange}
          showMargins={switchStates.showMargins}
          onShowMarginsChange={handleShowMarginsChange}
          onFormatChange={handleFormatChange}
          activeDecimalFormat={activeDecimalFormat}
          activeScaleFormat={activeScaleFormat}
        >
          {renderReportsContent(selectedStock)}
        </ReportFilters>
      ),
    },
    {
      key: "cash_flow",
      label: "Cash Flow",
      children: (
        <ReportFilters
          selectedStock={selectedStock}
          selectedPeriod={selectedPeriod}
          onPeriodChange={handlePeriodChange}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          periodOptions={periodOptions}
          reverseDate={switchStates.reverseDate}
          onReverseDateChange={handleReverseDateChange}
          showMargins={switchStates.showMargins}
          onShowMarginsChange={handleShowMarginsChange}
          onFormatChange={handleFormatChange}
          activeDecimalFormat={activeDecimalFormat}
          activeScaleFormat={activeScaleFormat}
        >
          {renderReportsContent(selectedStock)}
        </ReportFilters>
      ),
    },
  ];

  console.log("Tab Items: ", tabItems);

  return (
    <div className="vi-glass-card vi-fin-card w-full p-6">
      {/* Header */}
      <div className={isHeaderCollapsed ? "mb-0" : "mb-6"}>
        <div className="flex items-center justify-end">
          <button
            onClick={() => setIsHeaderCollapsed(!isHeaderCollapsed)}
            className="vi-glass-button w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-colors"
            aria-label={isHeaderCollapsed ? "Expand" : "Collapse"}
          >
            {isHeaderCollapsed ? (
              <ChevronDown className="w-3.5 h-3.5" />
            ) : (
              <ChevronUp className="w-3.5 h-3.5" />
            )}
          </button>
        </div>

        {!isHeaderCollapsed && (
          <>
            {selectedStock && profileData ? (
              <div className="flex flex-col items-center text-center md:flex-row md:items-start md:gap-4 md:text-left">
                <div className="w-20 h-20 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0 mb-3 md:mb-0">
                  <img
                    src={`/${selectedStock.company_logo}`}
                    alt={selectedStock.company_logo}
                    className="w-full h-full object-contain"
                  />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-gray-900 mb-1">
                    {profileData.company_name}
                  </h1>
                  <p className="text-sm text-gray-600">{profileData.profile}</p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center text-center md:flex-row md:items-start md:gap-4 md:text-left animate-pulse">
                <div className="w-20 h-20 rounded-full bg-gray-200 flex-shrink-0 mb-3 md:mb-0"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-5 bg-gray-200 rounded w-48 mx-auto md:mx-0"></div>
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6 mx-auto md:mx-0"></div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Tabs
        defaultActiveKey="income_statement"
        activeKey={activeTab}
        items={tabItems}
        onChange={handleTabChange}
        className="custom-financial-tabs"
      />
    </div>
  );
};

export default FinancialReports;
